import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { GATE, useSession } from "@/auth/session";

import { PageHead } from "@/components/PageHead";
import { Select, SearchInput } from "@/components/inputs";

import { ActionRow } from "@/components/ActionRow";
/** API Console — the demo surface: a CURATED set of endpoints (the ones a
 *  customer integration actually uses), each with editable inputs, the exact
 *  curl to copy, and a live "Send" against this deployment.
 *
 *  Requests run with the signed-in session; the curl shows the Bearer-key
 *  form customers will use once API keys ship (phase-2 security). Write
 *  endpoints are marked and default to safe demo values (BENCH- prefixes,
 *  transfer defaults to preview). */

interface Ep {
  id: string;
  group: string;
  method: "GET" | "POST" | "DELETE";
  path: string;                       // may contain {placeholders}
  title: string;
  desc: string;
  writes?: boolean;
  pathParams?: { name: string; demo: string }[];
  query?: { name: string; demo: string; hint?: string; opts?: string[] }[];
  body?: unknown;                     // demo JSON (editable in the textarea)
}

const ENDPOINTS: Ep[] = [
  { id: "cat-stats", group: "Asset Catalog", method: "GET", path: "/modules/asset-catalog/stats",
    title: "Catalog stats", desc: "Model counts by lifecycle - a cheap liveness check for integrations." },
  { id: "cat-models", group: "Asset Catalog", method: "GET", path: "/modules/asset-catalog/models",
    title: "List models", desc: "Model versions with key specs and derived facts. Filter by kind, status, search.",
    query: [{ name: "kind", demo: "battery", opts: ["vehicle", "battery", "cell", "bms", "bms_c", "motor", "mcu", "dcdc", "vcu", "vcu_c", "cluster", "cluster_c", "tcu"] },
      { name: "status", demo: "active", opts: ["draft", "active", "retired"] }, { name: "limit", demo: "20" }] },
  { id: "reg-parts", group: "Registry - read", method: "GET", path: "/modules/registry/parts",
    title: "List parts", desc: "Serialised components: model, serial, Connection ID, BLE MAC, status, installed-in.",
    query: [{ name: "kind", demo: "", opts: ["tcu", "bms_c", "vcu_c", "cluster_c", "cell", "bms", "motor", "mcu", "dcdc", "vcu", "cluster"] },
      { name: "state", demo: "inventory", opts: ["inventory", "deployed", "repair", "scrapped"] }, { name: "search", demo: "" }, { name: "limit", demo: "20" }] },
  { id: "reg-assets", group: "Registry - read", method: "GET", path: "/modules/registry/assets",
    title: "List assets", desc: "Vehicles (VIN + number plate) and batteries (BIN) with model and company.",
    query: [{ name: "asset_type", demo: "", opts: ["vehicle", "battery"] }, { name: "search", demo: "" }, { name: "limit", demo: "20" }] },
  { id: "reg-asset", group: "Registry - read", method: "GET", path: "/modules/registry/assets/{asset_id}",
    title: "Asset detail", desc: "One asset with its BOM slots (catalog-expected vs installed) and history.",
    pathParams: [{ name: "asset_id", demo: "BENCH-VIN-001" }] },
  { id: "reg-driver-get", group: "Registry - read", method: "GET", path: "/modules/registry/assets/{asset_id}/driver",
    title: "Asset's driver", desc: "Current holder and the closed-interval history.",
    pathParams: [{ name: "asset_id", demo: "BENCH-VIN-001" }] },
  { id: "enroll-parts", group: "Registry - write", method: "POST", path: "/modules/registry/parts/enroll",
    title: "Enroll parts", desc: "All-or-nothing. Connected kinds REQUIRE connection_id (IMEI); a model with the BLE tick requires ble_mac. Returns the broker credential to bake for new connections.",
    writes: true,
    body: { rows: [{ kind: "tcu", version_id: "<catalog model uuid>", serial_no: "BENCH-TCU-DEMO-1",
      connection_id: "999000000000001", ble_mac: null, board_id: null, iccid: null, imsi: null,
      manufactured_at: "2026-09-01", warranty_until: null, lot: null }] } },
  { id: "enroll-assets", group: "Registry - write", method: "POST", path: "/modules/registry/assets/enroll-bulk",
    title: "Enroll assets", desc: "All-or-nothing. Vehicles require label (number plate); batteries must not carry one.",
    writes: true,
    body: { rows: [{ asset_id: "BENCH-VIN-DEMO-1", version_id: "<vehicle model uuid>", label: "MH-12-DM-0001" }] } },
  { id: "transfer", group: "Registry - write", method: "POST", path: "/modules/registry/transfer",
    title: "Transfer ownership (any identifier)", desc: "One door: IMEI, board id, VIN/BIN or part serial. An asset moves its installed parts and their connections. ship=true (default) also moves CUSTODY; untick for sold-but-not-shipped. preview=true classifies WITHOUT writing - the demo default.",
    writes: true,
    body: { identifiers: ["BENCH-VIN-001"], company_id: "<company uuid>", preview: true } },
  { id: "install", group: "Registry - write", method: "POST", path: "/modules/registry/parts/{part_id}/install",
    title: "Install part into asset", desc: "Slot rules come from the catalog BOM; a different model than expected installs with a warning.",
    writes: true, pathParams: [{ name: "part_id", demo: "<part uuid>" }],
    body: { asset_id: "BENCH-VIN-001", slot: "cluster" } },
  { id: "driver-set", group: "Registry - write", method: "POST", path: "/modules/registry/assets/{asset_id}/driver",
    title: "Allocate asset to driver", desc: "Driver by id, DRV code or phone. Only ACTIVE drivers; the asset must belong to a company.",
    writes: true, pathParams: [{ name: "asset_id", demo: "BENCH-VIN-001" }],
    body: { driver: "DRV-2026-00001" } },
];

const METHOD_COLOR: Record<string, string> = { GET: "var(--info)", POST: "var(--ok)", DELETE: "var(--bad)" };

/** Which permission gates an endpoint, derived from its path.
 *
 *  The console calls the REAL routes with the session cookie
 *  (`credentials: "same-origin"`), so every request already passes through that
 *  route's own `require_cap` on the server — GATE.API buys entry to this page,
 *  never to the endpoints listed on it. This function changes no permission; it
 *  stops the page ADVERTISING calls the caller would only get a 403 from, which
 *  is both better UX and one less map of what exists handed to someone who
 *  cannot use it.
 */
function gateForPath(path: string): number | undefined {
  if (path.startsWith("/modules/registry")) return GATE.REGISTRY;
  if (path.startsWith("/modules/asset-catalog")) return GATE.ASSET_CATALOG;
  if (path.startsWith("/modules/asset-data")) return GATE.ASSET_DATA;
  if (path.startsWith("/modules/drivers")) return GATE.DRIVERS;
  if (path.startsWith("/modules/fota")) return GATE.FOTA;
  if (path.startsWith("/modules/link")) return GATE.LINK;
  if (path.startsWith("/modules/audit")) return GATE.AUDIT;
  return undefined;               // ungated or not a module route
}

function buildPath(ep: Ep, pathVals: Record<string, string>, queryVals: Record<string, string>): string {
  let p = ep.path;
  for (const pp of ep.pathParams ?? []) p = p.replace(`{${pp.name}}`, encodeURIComponent(pathVals[pp.name] ?? ""));
  const qs = (ep.query ?? []).map((q) => [q.name, queryVals[q.name] ?? ""] as const).filter(([, v]) => v);
  return qs.length ? `${p}?${qs.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&")}` : p;
}

export function ApiConsolePage() {
  // self-audit: every listed endpoint must exist in the LIVE spec — a missing
  // one renders red instead of silently lying to a customer
  const { can } = useSession();
  const spec = useQuery({ queryKey: ["openapi"], queryFn: () => fetch("/openapi.json").then((r) => r.json()), staleTime: 5 * 60_000 });
  const inSpec = (e: Ep) => !spec.data || !!spec.data.paths?.[e.path]?.[e.method.toLowerCase()];
  const [sel, setSel] = useState(ENDPOINTS[0].id);
  const ep = ENDPOINTS.find((e) => e.id === sel)!;
  const [pathVals, setPathVals] = useState<Record<string, string>>({});
  const [queryVals, setQueryVals] = useState<Record<string, string>>({});
  const [custom, setCustom] = useState<Record<string, boolean>>({});          // "Outside the list…" per field
  const [bodyText, setBodyText] = useState("");
  const [resp, setResp] = useState<{ status: number; ms: number; body: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const pick = (id: string) => {
    const e = ENDPOINTS.find((x) => x.id === id)!;
    setSel(id); setResp(null);
    setPathVals(Object.fromEntries((e.pathParams ?? []).map((p) => [p.name, p.demo])));
    setQueryVals(Object.fromEntries((e.query ?? []).map((q) => [q.name, q.demo])));
    setCustom({});
    setBodyText(e.body ? JSON.stringify(e.body, null, 2) : "");
  };
  useMemo(() => pick(ENDPOINTS[0].id), []);   // eslint-disable-line react-hooks/exhaustive-deps

  const url = buildPath(ep, pathVals, queryVals);
  const curl = [
    `curl -X ${ep.method} 'https://${window.location.host}${url}'`,
    `  -H 'Authorization: Bearer <YOUR_API_KEY>'`,
    ...(ep.body ? [`  -H 'Content-Type: application/json'`, `  -d '${bodyText.replace(/\n\s*/g, " ")}'`] : []),
  ].join(" \\\n");

  const send = async () => {
    setBusy(true); setResp(null);
    const t0 = performance.now();
    try {
      const r = await fetch(url, {
        method: ep.method, credentials: "same-origin",
        // tagged so the audit trail records console requests even when they are
        // successful reads — Send then shows up on the Activity tab immediately
        headers: { "X-Orbit-Console": "1", ...(ep.body ? { "Content-Type": "application/json" } : {}) },
        body: ep.body ? bodyText : undefined,
      });
      const text = await r.text();
      let pretty = text;
      try { pretty = JSON.stringify(JSON.parse(text), null, 2); } catch { /* not json */ }
      setResp({ status: r.status, ms: Math.round(performance.now() - t0), body: pretty });
    } catch (e) {
      setResp({ status: 0, ms: Math.round(performance.now() - t0), body: String(e) });
    } finally { setBusy(false); }
  };

  const visible = ENDPOINTS.filter((e) => {
    const g = gateForPath(e.path);
    return !g || can(g);
  });
  const groups = Array.from(new Set(visible.map((e) => e.group)));
  const pre: React.CSSProperties = { background: "var(--well)", border: "1px solid var(--line)", borderRadius: 8, padding: 10, fontSize: 11.5, overflowX: "auto", margin: 0, whiteSpace: "pre" };

  return (
    <div className="mpage on">
      <PageHead eyebrow="API · Console" title="API Console"
        desc="Live requests against this deployment, using your session. The curl shows the Bearer-key form integrators will use once customer API keys ship. Endpoints marked WRITE change data - demo bodies use BENCH- identifiers and transfer defaults to preview." />

      <div style={{ display: "grid", gridTemplateColumns: "260px minmax(0,1fr)", gap: 16, alignItems: "start" }}>
        {/* endpoint list */}
        <div className="panel" style={{ padding: 8 }}>
          {groups.map((g) => (
            <div key={g}>
              <div className="section-label" style={{ padding: "8px 8px 2px" }}>{g}</div>
              {visible.filter((e) => e.group === g).map((e) => (
                <button key={e.id} onClick={() => pick(e.id)}
                  style={{ display: "flex", gap: 8, alignItems: "center", width: "100%", padding: "7px 8px",
                    background: e.id === sel ? "var(--well)" : "none", border: "none", borderRadius: 8,
                    cursor: "pointer", textAlign: "left", color: "inherit" }}>
                  <span className="mono" style={{ color: METHOD_COLOR[e.method], fontWeight: 800, fontSize: 10, width: 34 }}>{e.method}</span>
                  <span style={{ fontSize: 12.5 }}>{e.title}</span>
                  {!inSpec(e) && <span className="chip bad" style={{ marginLeft: "auto", fontSize: 9 }}>NOT IN API</span>}
                  {inSpec(e) && e.writes && <span className="chip warn" style={{ marginLeft: "auto", fontSize: 9 }}>WRITE</span>}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* request builder */}
        <div style={{ minWidth: 0 }}>
          <div className="panel panel-pad">
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <span className="mono" style={{ color: METHOD_COLOR[ep.method], fontWeight: 800 }}>{ep.method}</span>
              <span className="mono" style={{ fontSize: 13, color: "var(--t-bright)" }}>{ep.path}</span>
              {!inSpec(ep) && <span className="chip bad">NOT IN THE RUNNING API - console out of date</span>}
              {inSpec(ep) && ep.writes && <span className="chip warn">WRITES DATA</span>}
            </div>
            <p style={{ fontSize: 12.5, color: "var(--t-mute)", margin: "8px 0 0", lineHeight: 1.6 }}>{ep.desc}</p>

            {(ep.pathParams ?? []).map((p) => (
              <div key={p.name} style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10 }}>
                <span className="mono" style={{ fontSize: 12, color: "var(--t-mute)", width: 110 }}>{p.name}</span>
                <SearchInput mono value={pathVals[p.name] ?? ""} style={{ flex: 1, minWidth: 0 }}
                  onChange={(e) => setPathVals((v) => ({ ...v, [p.name]: e.target.value }))} />
              </div>
            ))}
            {(ep.query ?? []).map((q) => (
              <div key={q.name} style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10 }}>
                <span className="mono" style={{ fontSize: 12, color: "var(--t-mute)", width: 110 }}>{q.name}</span>
                {q.opts && !custom[q.name] ? (
                  /* vocabulary field: pick from the real options — or step outside
                     the list on purpose to demo the API's validation error */
                  <Select value={queryVals[q.name] ?? ""} style={{ flex: 1, minWidth: 0 }}
                    onChange={(e) => {
                      if (e.target.value === "__custom__") { setCustom((c) => ({ ...c, [q.name]: true })); setQueryVals((v) => ({ ...v, [q.name]: "" })); }
                      else setQueryVals((v) => ({ ...v, [q.name]: e.target.value }));
                    }}>
                    <option value=""> - omit - </option>
                    {q.opts.map((o) => <option key={o} value={o}>{o}</option>)}
                    <option value="__custom__">✎ Outside the list… (test what the API returns)</option>
                  </Select>
                ) : (
                  <>
                    <SearchInput mono value={queryVals[q.name] ?? ""} placeholder={q.opts ? "anything - the API will judge it" : q.hint}
                      style={{ flex: 1, minWidth: 0 }}
                      onChange={(e) => setQueryVals((v) => ({ ...v, [q.name]: e.target.value }))} />
                    {q.opts && (
                      <button className="btn ghost" title="Back to the valid options"
                        onClick={() => { setCustom((c) => ({ ...c, [q.name]: false })); setQueryVals((v) => ({ ...v, [q.name]: q.demo })); }}>⌄ list</button>
                    )}
                  </>
                )}
              </div>
            ))}
            {ep.body != null && (
              <>
                <div className="section-label tight">Request body (editable)</div>
                <textarea className="mono" value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={Math.min(14, bodyText.split("\n").length + 1)}
                  style={{ width: "100%", background: "var(--well)", color: "var(--t-bright)", border: "1px solid var(--line)",
                    borderRadius: 8, padding: 10, fontSize: 11.5, resize: "vertical" }} />
              </>
            )}
            <ActionRow top={12}>
              <button className="btn teal" disabled={busy || !inSpec(ep)} onClick={() => void send()}>{busy ? "Sending…" : "Send request"}</button>
              {resp && (
                <span className="mono" style={{ fontSize: 12, color: resp.status >= 200 && resp.status < 300 ? "var(--ok)" : "var(--bad)" }}>
                  {resp.status || "network error"} · {resp.ms} ms
                </span>
              )}
            </ActionRow>
          </div>

          <div className="section-label tight">What the customer runs</div>
          <div className="panel panel-pad">
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
              <button className="btn ghost" onClick={() => { void navigator.clipboard?.writeText(curl); }}>Copy curl</button>
            </div>
            <pre className="mono" style={pre}>{curl}</pre>
          </div>

          {resp && (
            <>
              <div className="section-label tight">Response</div>
              <div className="panel panel-pad">
                <pre className="mono" style={{ ...pre, maxHeight: 420, overflowY: "auto" }}>{resp.body}</pre>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
